/**
 * La plantilla de «Qué les decís»: las plantillas de la cuenta agrupadas por estado (aprobadas, en revisión, rechazadas,
 * borradores y las que no sirven con esta cuenta), con un buscador, y la previa de la elegida con su estado de Meta.
 * Crear, partir de un modelo, «Duplicar y editar» y «Seguir editando» van a la página del editor: lo decide quien la
 * muestra (`onCrear`…). Sus variables van debajo, en `PasoVariables`. El nombre de la campaña se cambia en «Tu campaña».
 */
import { useId, useState } from "react";
import type { ReactNode } from "react";
import { Copy, LayoutTemplate, Loader2, Pencil, Plus, Search } from "lucide-react";
import type { ModoTransporte, PlantillaDetalle, PlantillaResumen } from "../../types";
import { esInutilizable } from "../../logic/asistente";
import { conEjemplos, desdeDetalle, notaDeEjemplos, previaLocal } from "../../logic/plantilla";
import { CATEGORIA, CHIP_PLANTILLA, motivoPlantillaNoUsable } from "../../logic/textos";
import { usdPorMensaje } from "../../logic/formato";
import { normalizarTexto } from "../../logic/base";
import { PreviaWhatsApp } from "../PreviaWhatsApp";
import { ChipEstadoPlantilla } from "../ChipEstadoPlantilla";

export function PasoPlantilla({
  plantillas, error, plantillaId, onElegir, detalle, errorDetalle, modo, asociadaId,
  onCrear, onModelos, onDuplicar, onSeguirEditando, duplicandoId, estadoEnVivo,
}: {
  plantillas: PlantillaResumen[] | null; error: string;
  plantillaId: string; onElegir: (p: PlantillaResumen) => void; detalle?: PlantillaDetalle; errorDetalle: string;
  modo: ModoTransporte | null;
  /** La plantilla que la campaña ya tiene guardada: se marca aunque sea un borrador o esté rechazada. */
  asociadaId: string;
  onCrear: () => void; onModelos: () => void; onDuplicar: (id: string) => void; onSeguirEditando: (id: string) => void;
  /** La plantilla que se está duplicando ("" si ninguna): apaga los botones y gira en el que se tocó. */
  duplicandoId: string;
  /** `EstadoPlantillaEnVivo` de la elegida: va debajo de la previa. */
  estadoEnVivo: ReactNode;
}) {
  const idBuscar = useId();
  const [buscar, setBuscar] = useState("");
  const buscado = normalizarTexto(buscar);
  const todas = (plantillas || []).filter((p) => !p.archivada);
  // la elegida queda a la vista aunque no coincida con lo buscado
  const vivas = buscado ? todas.filter((p) => p.id === plantillaId || normalizarTexto(p.nombreVisible).includes(buscado)) : todas;
  const inutil = (p: PlantillaResumen) => esInutilizable(p, modo);
  const usables = (p: PlantillaResumen) => !inutil(p) && (p.estado === "approved" || p.estado === "received" || p.estado === "pending");
  const aprobadas = vivas.filter((p) => usables(p) && p.estado === "approved");
  const enRevision = vivas.filter((p) => usables(p) && p.estado !== "approved");
  const rechazadas = vivas.filter((p) => p.estado === "rejected");
  const borradores = vivas.filter((p) => p.estado === "borrador" || p.estado === "unsubmitted");
  // Aprobadas (o en revisión) que no sirven con la cuenta conectada: nunca aparecen entre las elegibles.
  const noUsables = vivas.filter((p) => inutil(p) && (p.estado === "approved" || p.estado === "received" || p.estado === "pending"));
  const b = detalle ? desdeDetalle(detalle) : null;
  const marcada = (id: string) => plantillaId === id || (!plantillaId && asociadaId === id);

  const botonChico = "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
  // Una sola acción con un solo nombre, en todos lados (antes: «Duplicar y ajustar», «Duplicar y corregir», «Corregir»…)
  const botonDuplicar = (p: PlantillaResumen) => (
    <button type="button" onClick={() => onDuplicar(p.id)} disabled={!!duplicandoId} className={botonChico}>
      {duplicandoId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />} Duplicar y editar
    </button>
  );
  // En modo simulado una simulada se puede elegir (es la que se usa para probar): el chip avisa igual qué es.
  const chips = (p: PlantillaResumen) => (
    <>
      {p.simulado === true && <ChipPlantilla tipo="simulada" />}
      {p.deOtraCuenta === true && <ChipPlantilla tipo="deOtraCuenta" />}
    </>
  );
  const filaRadio = (p: PlantillaResumen, sub: ReactNode, extra?: ReactNode) => (
    <li key={p.id} className="flex items-center gap-2">
      <label className={`flex flex-1 min-w-0 items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${marcada(p.id) ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-slate-300"}`}>
        <input type="radio" name="plantilla" checked={marcada(p.id)} onChange={() => onElegir(p)} className="mt-1 accent-violet-600" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">{p.nombreVisible}</span>
            {chips(p)}
          </span>
          <span className="block text-xs text-slate-500">{sub}</span>
        </span>
      </label>
      {extra}
    </li>
  );
  // Costo real (Meta + Twilio) y, si hay recargo, lo que se le cobra a la marca.
  const costo = (p: PlantillaResumen) => {
    const marca = usdPorMensaje(p.costoPorMensajeClienteUSD);
    // Sin el costo real (una persona de un cliente), sólo su precio.
    const real = p.costoPorMensajeUSD === undefined ? marca : usdPorMensaje(p.costoPorMensajeUSD);
    return `${(CATEGORIA as Record<string, string>)[p.categoria] || p.categoria} · ${real} por mensaje${marca !== real ? ` · a la marca ${marca}` : ""}`;
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)]">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onCrear}
            className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100">
            <Plus size={14} /> Crear plantilla nueva
          </button>
          <button type="button" onClick={onModelos}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <LayoutTemplate size={14} /> Partir de un modelo Deenex
          </button>
        </div>

        {todas.length > 0 && (
          <div className="relative">
            <label htmlFor={idBuscar} className="sr-only">Buscar una plantilla</label>
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input id={idBuscar} type="search" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar una plantilla"
              className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
        )}

        {error ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">No pudimos traer las plantillas. {error}</p>
        ) : !plantillas ? (
          <div className="h-32 rounded-xl bg-slate-100 animate-pulse" />
        ) : (
          <>
            {buscado && !vivas.length ? (
              <p className="text-sm text-slate-500">Ninguna plantilla se llama así.</p>
            ) : !aprobadas.length && !enRevision.length && !buscado && (
              <p className="rounded-xl border-2 border-dashed border-slate-200 p-5 text-center text-sm text-slate-600">
                Todavía no tenés plantillas aprobadas. Creá una acá mismo: mientras Meta la revisa, podés completar las variables y elegir cuándo sale.
              </p>
            )}
            {aprobadas.length > 0 && (
              <fieldset>
                <legend className="text-xs font-semibold text-slate-600 mb-2">Aprobadas</legend>
                <ul className="space-y-2">
                  {aprobadas.map((p) => filaRadio(p, costo(p), botonDuplicar(p)))}
                </ul>
              </fieldset>
            )}
            {enRevision.length > 0 && (
              <fieldset>
                <legend className="text-xs font-semibold text-slate-600 mb-2">En revisión</legend>
                <ul className="space-y-2">
                  {/* mientras Meta la revisa no se edita: si tiene un error, se duplica y se corrige la copia (Llama Leads, 26/10/2026) */}
                  {enRevision.map((p) => filaRadio(p, <><ChipEstadoPlantilla estado={p.estado} /> <span className="ml-1">{costo(p)}</span></>, botonDuplicar(p)))}
                </ul>
              </fieldset>
            )}
            {rechazadas.length > 0 && (
              <section aria-label="Rechazadas">
                <p className="text-xs font-semibold text-slate-600 mb-2">Rechazadas</p>
                <ul className="space-y-2">
                  {rechazadas.map((p) => (
                    <li key={p.id} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${marcada(p.id) ? "border-red-300 bg-red-50/60" : "border-slate-200"}`}>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-800">{p.nombreVisible}{marcada(p.id) && <span className="ml-2 text-xs font-normal text-slate-500">(la de esta campaña)</span>}</span>
                        <span className="block text-xs text-red-700">Meta la rechazó: {p.rejectionReason?.trim() || "no dio el motivo"}.</span>
                      </span>
                      {botonDuplicar(p)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {borradores.length > 0 && (
              <section aria-label="Borradores">
                <p className="text-xs font-semibold text-slate-600 mb-2">Borradores</p>
                <ul className="space-y-2">
                  {borradores.map((p) => p.estado === "unsubmitted"
                    // Se elige para reintentar el envío desde el estado en vivo (a la derecha).
                    ? filaRadio(p, "No se pudo mandar a Meta: elegila para reintentar el envío.")
                    : (
                      <li key={p.id} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${marcada(p.id) ? "border-violet-300 bg-violet-50/60" : "border-slate-200"}`}>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-800">{p.nombreVisible}{marcada(p.id) && <span className="ml-2 text-xs font-normal text-slate-500">(la de esta campaña)</span>}</span>
                          <span className="block text-xs text-slate-500">Todavía no se mandó a Meta.</span>
                        </span>
                        <button type="button" onClick={() => onSeguirEditando(p.id)} className={botonChico}>
                          <Pencil size={12} /> Seguir editando
                        </button>
                      </li>
                    ))}
                </ul>
              </section>
            )}
            {noUsables.length > 0 && (
              <section aria-label="No se pueden usar con esta cuenta">
                <p className="text-xs font-semibold text-slate-600 mb-2">No se pueden usar con esta cuenta</p>
                <ul className="space-y-2">
                  {noUsables.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-500">{p.nombreVisible}</span>
                          {chips(p)}
                        </span>
                        <span className="block text-xs text-amber-800">{motivoPlantillaNoUsable(p, modo)}</span>
                      </span>
                      {botonDuplicar(p)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
      <div className="space-y-3">
        {plantillaId && b ? (
          <>
            <PreviaWhatsApp compacto texto={previaLocal(b)} mediaUrl={b.mediaUrl ? conEjemplos(b.mediaUrl, b) : undefined} botones={b.botones}
              pie={b.tipo === "whatsapp/card" ? b.pie : undefined} encabezadoTexto={b.encabezadoTexto} />
            {notaDeEjemplos(b) && <p className="text-xs text-slate-500" data-testid="nota-ejemplos">{notaDeEjemplos(b)}</p>}
          </>
        ) : plantillaId && errorDetalle ? (
          <p className="text-sm text-red-700">{errorDetalle}</p>
        ) : plantillaId ? (
          <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
        ) : (
          <p className="text-sm text-slate-400">Elegí una plantilla para ver cómo queda.</p>
        )}
        {estadoEnVivo}
      </div>
    </div>
  );
}

/** Chip «Simulada» o «De otra cuenta» de una plantilla (A1-4, A1-6). */
function ChipPlantilla({ tipo }: { tipo: keyof typeof CHIP_PLANTILLA }) {
  const c = CHIP_PLANTILLA[tipo];
  return <span title={c.ayuda} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>;
}

/** Lo que se pregunta al cambiar la plantilla de una campaña con variables elegidas (en línea, con Sí/No). */
export const PREGUNTA_CAMBIO_PLANTILLA = "Cambiar la plantilla borra las variables que elegiste. ¿Cambiar igual?";

/** La pregunta, debajo de las plantillas (antes, `window.confirm`): «Sí» guarda la plantilla nueva y «No» vuelve a la guardada. */
export function PreguntaCambioPlantilla({ onSi, onNo, guardando }: { onSi: () => void; onNo: () => void; guardando: boolean }) {
  return (
    <div role="group" aria-labelledby="pregunta-cambio-plantilla" data-testid="pregunta-cambio-plantilla"
      className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <p id="pregunta-cambio-plantilla" className="flex-1 min-w-[200px]">{PREGUNTA_CAMBIO_PLANTILLA}</p>
      <div className="flex gap-2">
        <button type="button" onClick={onSi} disabled={guardando}
          className="h-11 min-w-[64px] rounded-lg bg-amber-600 px-4 font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
          Sí
        </button>
        <button type="button" onClick={onNo} disabled={guardando}
          className="h-11 min-w-[64px] rounded-lg border border-amber-300 bg-white px-4 font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
          No
        </button>
      </div>
    </div>
  );
}
